<template>
  <div class="table-content">
    <div class="table-wrap">
      <div class="table-title-div">
        <p class="table-title">{{ title }}</p>
        <div class="qaqc-scorecard-header-actions">
          <div class="csv-btn" @click="downloadTableCSV">
            <v-img class="pointer" :src="documentDownload" alt="csv download"></v-img>
          </div>
          <v-btn
            icon
            variant="text"
            size="small"
            class="qaqc-scorecard-collapse-btn"
            :aria-label="isCollapsed ? 'Expand' : 'Collapse'"
            @click="isCollapsed = !isCollapsed"
          >
            <v-icon>{{ isCollapsed ? 'mdi-chevron-down' : 'mdi-chevron-up' }}</v-icon>
          </v-btn>
        </div>
      </div>
      <div v-show="!isCollapsed" class="qaqc-table-content">
        <p class="qaqc-table-description">{{ description }}</p>
      <v-table class="test-data-table qaqc-table-wrap">
        <thead>
          <tr class="main-header">
            <th class="sticky-header-sub">Validation Check</th>
            <th class="sticky-header-sub text-center">Passed</th>
            <th class="sticky-header-sub text-center">Failed</th>
            <th class="sticky-header-sub text-center">Pass Rate</th>
          </tr>
        </thead>
        <tbody>
          <template v-for="(row, idx) in rows" :key="idx">
            <tr>
              <td>{{ row.check }}</td>
              <td class="text-center">{{ row.passed }}</td>
              <td
                class="text-center qaqc-validation-matrix-failed-cell"
                :class="{ 'qaqc-validation-matrix-failed-cell--interactive': hasFailedCount(row) }"
                role="button"
                :tabindex="hasFailedCount(row) ? 0 : -1"
                @click="hasFailedCount(row) ? toggleFailedRow(idx) : null"
                @keydown.enter.prevent="hasFailedCount(row) ? toggleFailedRow(idx) : null"
                @keydown.space.prevent="hasFailedCount(row) ? toggleFailedRow(idx) : null"
              >
                <span class="qaqc-validation-matrix-failed-inner">
                  <v-icon
                    v-if="hasFailedCount(row)"
                    size="small"
                    class="qaqc-validation-matrix-failed-chevron"
                  >{{ expandedRowIndex === idx ? 'mdi-chevron-up' : 'mdi-chevron-right' }}</v-icon>
                  <span class="qaqc-validation-matrix-failed-value">{{ row.failed }}</span>
                </span>
              </td>
              <td class="text-center">{{ row.passRate }}</td>
            </tr>
            <tr v-if="expandedRowIndex === idx && hasFailedCount(row)" class="qaqc-validation-matrix-expand-row">
              <td colspan="4" class="qaqc-validation-matrix-expand-cell">
                <div class="qaqc-validation-matrix-expand-inner">
                  <span class="qaqc-consistency-sublabel">Failed samples (click to view QC details)</span>
                  <div v-if="failedSamplesList(row).length" class="qaqc-consistency-chips">
                    <span
                      v-for="(s, sidx) in failedSamplesList(row)"
                      :key="matrixChipDomKey(idx, s, sidx)"
                      class="qaqc-consistency-chip qaqc-validation-matrix-failed-chip"
                      :class="matrixChipClasses(idx, s, sidx)"
                      role="button"
                      :tabindex="matrixChipTabindex(idx, s, sidx)"
                      :aria-busy="matrixChipIsLoading(idx, s, sidx)"
                      @click.stop="openSampleQc(s, idx, sidx)"
                      @keydown.enter.prevent="activateFailedSampleChip(s, idx, sidx)"
                      @keydown.space.prevent="activateFailedSampleChip(s, idx, sidx)"
                    >
                      <v-progress-circular
                        v-if="matrixChipIsLoading(idx, s, sidx)"
                        indeterminate
                        size="18"
                        width="2"
                        color="primary"
                        class="qaqc-validation-matrix-chip-loader"
                      />
                      <template v-else>{{ s.fluidSampleID || s.id }}</template>
                    </span>
                  </div>
                  <div v-else class="qaqc-validation-matrix-no-ids">No sample IDs returned for this check.</div>
                </div>
              </td>
            </tr>
          </template>
        </tbody>
      </v-table>
      </div>
    </div>
  </div>
</template>

<script>
import api from "@/axiosInterceptor";
import { getTenantPrefix } from "@/utils/tenant";

export default {
  name: "ValidationPassRateMatrixTable",
  props: {
    rows: { type: Array, default: () => [] },
    title: {
      type: String,
      default: "Validation Pass Rate Matrix",
    },
    description: {
      type: String,
      default: "Percentage of all CCE samples passing each validation check",
    },
    matrixTestContext: {
      type: String,
      default: "cce",
      validator: (v) => v === "cce" || v === "dl",
    },
  },
  data() {
    return {
      documentDownload: require("@/assets/Images/csv.svg"),
      isCollapsed: false,
      expandedRowIndex: null,
      loadingMatrixChipKey: null,
    };
  },
  watch: {
    rows() {
      this.expandedRowIndex = null;
      this.loadingMatrixChipKey = null;
    },
  },
  methods: {
    failedCountNum(row) {
      const raw = row?.failed;
      if (raw === undefined || raw === null || raw === "-") return 0;
      const n = Number(String(raw).replace(/[^0-9.-]/g, ""));
      return Number.isNaN(n) ? 0 : n;
    },
    hasFailedCount(row) {
      return this.failedCountNum(row) > 0;
    },
    failedSamplesList(row) {
      const list = row?.failedSamples;
      return Array.isArray(list) ? list : [];
    },
    toggleFailedRow(idx) {
      this.expandedRowIndex = this.expandedRowIndex === idx ? null : idx;
    },
    resolveTenantId() {
      return localStorage.getItem("tenantId") || getTenantPrefix();
    },
    pushQaqcUnifiedTestRoute(routeTestType, testId) {
      const tenantId = this.resolveTenantId();
      const tid = testId != null ? String(testId) : "";
      if (!tenantId || !routeTestType || !tid) return;
      const path = `/${tenantId}/qaqc/test/${routeTestType}/${tid}`;
      this.$router.push(path);
    },
    matrixChipInstanceKey(rowIdx, s, chipIdx) {
      return `r${rowIdx}:i${chipIdx}:${String(s.id ?? "")}:f${String(s.fluidSampleID ?? "")}`;
    },
    matrixChipDomKey(rowIdx, s, chipIdx) {
      return this.matrixChipInstanceKey(rowIdx, s, chipIdx);
    },
    matrixChipIsLoading(rowIdx, s, chipIdx) {
      return this.loadingMatrixChipKey === this.matrixChipInstanceKey(rowIdx, s, chipIdx);
    },
    matrixChipIsBusyOther(rowIdx, s, chipIdx) {
      return this.loadingMatrixChipKey != null && !this.matrixChipIsLoading(rowIdx, s, chipIdx);
    },
    matrixChipClasses(rowIdx, s, chipIdx) {
      return {
        "qaqc-validation-matrix-failed-chip--loading": this.matrixChipIsLoading(rowIdx, s, chipIdx),
        "qaqc-validation-matrix-failed-chip--disabled": this.matrixChipIsBusyOther(rowIdx, s, chipIdx),
      };
    },
    matrixChipTabindex(rowIdx, s, chipIdx) {
      if (this.matrixChipIsLoading(rowIdx, s, chipIdx)) return -1;
      if (this.matrixChipIsBusyOther(rowIdx, s, chipIdx)) return -1;
      return 0;
    },
    activateFailedSampleChip(sample, rowIdx, chipIdx) {
      if (this.loadingMatrixChipKey) return;
      this.openSampleQc(sample, rowIdx, chipIdx);
    },
    async fetchSamplePayloadForMatrixChip(sample) {
      if (sample?.id) {
        const res = await api.get(`public/api/samples/${sample.id}?viewSummary=true`);
        return this.samplePayloadFromViewSummary(res);
      }
      const fid = sample?.fluidSampleID;
      if (!fid) return null;
      const res = await api.get("public/api/samples", {
        params: {
          fluidSampleID: `["${String(fid)}"]`,
          viewSummary: true,
          pageNumber: 1,
          pageSize: 10,
        },
      });
      const samples = res?.data?.data?.samples;
      return Array.isArray(samples) && samples[0] ? samples[0] : null;
    },
    samplePayloadFromViewSummary(res) {
      const d = res?.data?.data;
      if (!d) return null;
      if (d.sample && typeof d.sample === "object") return d.sample;
      const list = d.samples;
      if (Array.isArray(list) && list[0]) return list[0];
      return null;
    },
    async openSampleQc(sample, rowIdx, chipIdx) {
      const instanceKey = this.matrixChipInstanceKey(rowIdx, sample, chipIdx);
      if (!sample || this.loadingMatrixChipKey) return;
      if (!sample.id && !sample.fluidSampleID) return;
      this.loadingMatrixChipKey = instanceKey;
      try {
        const s = await this.fetchSamplePayloadForMatrixChip(sample);
        if (!s) return;
        const mongoId = s.ID || s._id || s.id;
        if (!mongoId) return;

        let testId = null;
        let routeTestType = null;

        if (this.matrixTestContext === "dl") {
          if (s.dltests?.length) {
            const t = s.dltests[0];
            testId = t.ID || t._id || t.id;
            routeTestType = "dltest";
          }
        } else if (s.ccetests?.length) {
          const t = s.ccetests[0];
          testId = t.ID || t._id || t.id;
          routeTestType = "ccetest";
        }

        if (!testId && s.dltests?.length) {
          const t = s.dltests[0];
          testId = t.ID || t._id || t.id;
          routeTestType = "dltest";
        }
        if (!testId && s.ccetests?.length) {
          const t = s.ccetests[0];
          testId = t.ID || t._id || t.id;
          routeTestType = "ccetest";
        }

        localStorage.setItem("sampleId", String(mongoId));
        if (testId && routeTestType) {
          localStorage.setItem("testId", testId);
          window.dispatchEvent(new CustomEvent("sampleIdChanged"));
          this.pushQaqcUnifiedTestRoute(routeTestType, testId);
        } else {
          const well = s.location?.well ?? s.wellName ?? s.well;
          if (well) {
            this.$router.push({ name: "QAQCDetailView", query: { well: String(well) } });
          } else {
            this.$router.push({ name: "QAQCDetailView" });
          }
        }
      } catch {
        // Network or API error; avoid noisy UI in overview
      } finally {
        this.loadingMatrixChipKey = null;
      }
    },
    downloadTableCSV() {
      if (!this.rows || this.rows.length === 0) {
        return;
      }
      const fileName = "validation_pass_rate_matrix.csv";
      const csvHeader = ["Validation Check", "Passed", "Failed", "Pass Rate", "Failed sample IDs"];
      const csvRows = this.rows.map((row) => {
        const ids = this.failedSamplesList(row)
          .map((s) => s.fluidSampleID || s.id)
          .join(";");
        return [
          String(row.check ?? ""),
          String(row.passed ?? ""),
          String(row.failed ?? ""),
          String(row.passRate ?? ""),
          ids,
        ];
      });
      const csvContent = [csvHeader.join(","), ...csvRows.map((row) => row.join(","))].join("\n");
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      if (link.download !== undefined) {
        const url = URL.createObjectURL(blob);
        link.setAttribute("href", url);
        link.setAttribute("download", fileName);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      }
    },
  },
};
</script>

<style scoped>
.qaqc-validation-matrix-failed-cell--interactive {
  cursor: pointer;
}

.qaqc-validation-matrix-failed-inner {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  color: #f44336;
  font-weight: 500;
}

.qaqc-validation-matrix-failed-chevron {
  color: #f44336 !important;
}

.qaqc-validation-matrix-expand-row {
  background: #fafafa;
}

.qaqc-validation-matrix-expand-cell {
  padding: 12px 16px !important;
  vertical-align: top;
}

.qaqc-validation-matrix-expand-inner {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.qaqc-validation-matrix-failed-chip {
  cursor: pointer;
  background: #fee2e2;
  border: 1px solid rgba(244, 67, 54, 0.25);
}

.qaqc-validation-matrix-failed-chip:hover:not(.qaqc-validation-matrix-failed-chip--loading):not(.qaqc-validation-matrix-failed-chip--disabled) {
  background: #fecaca;
}

.qaqc-validation-matrix-failed-chip--loading {
  cursor: wait;
  pointer-events: none;
  min-width: 40px;
  min-height: 32px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.qaqc-validation-matrix-failed-chip--disabled {
  cursor: not-allowed;
  pointer-events: none;
  opacity: 0.45;
}

.qaqc-validation-matrix-chip-loader {
  flex-shrink: 0;
}

.qaqc-validation-matrix-no-ids {
  font-size: 12px;
  color: #6b7280;
}
</style>
